<template>
  <div class="maintenance-shell">
    <div class="aurora aurora-a"></div>
    <div class="aurora aurora-b"></div>
    <div class="grid"></div>

    <div class="content">
      <div class="badge">Scheduled Platform Tune-Up</div>
      <h1>We are polishing CASS</h1>
      <p class="subtitle">
        Maintenance mode is active. While we are working, try a quick round of
        <strong>Route Runner</strong>.
      </p>

      <div class="game-panel">
        <div class="game-head">
          <span>Route Runner</span>
          <span>Score {{ displayScore }}</span>
          <span>Best {{ bestScore }}</span>
        </div>

        <div class="legend-row">
          <span class="legend-item legend-player">REQ packet: you</span>
          <span class="legend-item legend-outage">503 barrier: jump</span>
          <span class="legend-item legend-firewall">firewall gate: duck</span>
          <span class="legend-item legend-token">200 OK token: collect</span>
        </div>

        <div class="canvas-wrap" ref="gameArea">
          <canvas ref="gameCanvas" class="game-canvas"></canvas>
          <div v-if="!running" class="overlay">
            <h3>{{ gameOver ? "Incident detected" : "Ready to run" }}</h3>
            <p v-if="gameOver">{{ gameOverReason }}. Final score {{ displayScore }}</p>
            <p v-else>You are a REQ packet. Jump outages, duck firewalls, collect 200 OK.</p>
            <v-btn color="#006c27" dark @click="startGame">
              {{ gameOver ? "Play Again" : "Start" }}
            </v-btn>
          </div>
        </div>

        <div class="controls-row">
          <div class="desktop-hint">Desktop: Space/Up to jump, Down to duck.</div>
          <div class="mobile-controls">
            <v-btn
              small
              outlined
              color="#fbc02d"
              @touchstart.prevent="setDuck(true)"
              @touchend.prevent="setDuck(false)"
              @mousedown.prevent="setDuck(true)"
              @mouseup.prevent="setDuck(false)"
              @mouseleave="setDuck(false)"
            >
              Duck
            </v-btn>
            <v-btn
              small
              outlined
              color="#66bb6a"
              @touchstart.prevent="jump"
              @mousedown.prevent="jump"
            >
              Jump
            </v-btn>
          </div>
        </div>
      </div>

      <div class="hint">Please close this tab and come back later.</div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = "cass_maintenance_runner_best";

export default {
  name: "MaintenanceMode",
  data() {
    return {
      running: false,
      gameOver: false,
      gameOverReason: "",
      score: 0,
      bestScore: 0,
      canvasWidth: 720,
      canvasHeight: 260,
      rafId: null,
      lastTime: 0,
      speed: 300,
      gravity: 2300,
      spawnCooldown: 0,
      groundY: 0,
      duckPressed: false,
      jumpQueued: false,
      worldShiftA: 0,
      worldShiftB: 0,
      runner: {
        x: 94,
        y: 0,
        width: 40,
        height: 56,
        normalHeight: 56,
        duckHeight: 34,
        vy: 0,
        onGround: true,
      },
      obstacles: [],
      tokens: [],
      lastObstacleType: "",
    };
  },
  computed: {
    displayScore() {
      return Math.floor(this.score);
    },
  },
  mounted() {
    const best = Number(window.localStorage.getItem(STORAGE_KEY));
    if (!Number.isNaN(best) && best > 0) {
      this.bestScore = best;
    }
    this.setupCanvas();
    this.render();
    window.addEventListener("resize", this.setupCanvas);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },
  beforeDestroy() {
    this.stopLoop();
    window.removeEventListener("resize", this.setupCanvas);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  },
  methods: {
    setupCanvas() {
      const area = this.$refs.gameArea;
      const canvas = this.$refs.gameCanvas;
      if (!area || !canvas) {
        return;
      }

      const width = Math.max(320, area.clientWidth);
      const height = Math.min(320, Math.max(220, Math.round(width * 0.36)));
      this.canvasWidth = width;
      this.canvasHeight = height;
      this.groundY = this.canvasHeight - 46;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!this.running) {
        this.render();
      }
    },
    resetState() {
      this.score = 0;
      this.speed = 300;
      this.spawnCooldown = 0.95;
      this.obstacles = [];
      this.tokens = [];
      this.duckPressed = false;
      this.jumpQueued = false;
      this.worldShiftA = 0;
      this.worldShiftB = 0;
      this.runner.width = 40;
      this.runner.height = this.runner.normalHeight;
      this.runner.y = this.groundY - this.runner.height;
      this.runner.vy = 0;
      this.runner.onGround = true;
      this.lastObstacleType = "";
    },
    startGame() {
      this.stopLoop();
      this.gameOver = false;
      this.gameOverReason = "";
      this.running = true;
      this.lastTime = 0;
      this.resetState();
      this.loop(performance.now());
    },
    stopLoop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.running = false;
    },
    loop(timestamp) {
      if (!this.running) {
        return;
      }

      if (this.lastTime === 0) {
        this.lastTime = timestamp;
      }
      let dt = (timestamp - this.lastTime) / 1000;
      this.lastTime = timestamp;
      if (dt > 0.05) {
        dt = 0.05;
      }

      this.update(dt);
      this.render();
      this.rafId = requestAnimationFrame(this.loop);
    },
    update(dt) {
      this.speed = Math.min(560, this.speed + dt * 7);
      this.score += dt * 11;

      this.worldShiftA = (this.worldShiftA + this.speed * dt * 0.12) % this.canvasWidth;
      this.worldShiftB = (this.worldShiftB + this.speed * dt * 0.26) % this.canvasWidth;

      if (this.jumpQueued && this.runner.onGround) {
        this.runner.vy = -760;
        this.runner.onGround = false;
      }
      this.jumpQueued = false;

      if (this.duckPressed && this.runner.onGround) {
        this.runner.height = this.runner.duckHeight;
      } else {
        this.runner.height = this.runner.normalHeight;
      }

      this.runner.vy += this.gravity * dt;
      this.runner.y += this.runner.vy * dt;

      const runnerGroundY = this.groundY - this.runner.height;
      if (this.runner.y >= runnerGroundY) {
        this.runner.y = runnerGroundY;
        this.runner.vy = 0;
        this.runner.onGround = true;
      }

      this.spawnCooldown -= dt;
      if (this.spawnCooldown <= 0) {
        this.spawnPattern();
        const minGap = Math.max(0.55, 1.15 - this.speed / 1200);
        const maxGap = Math.max(0.9, 1.65 - this.speed / 1400);
        this.spawnCooldown = minGap + Math.random() * (maxGap - minGap);
      }

      for (let i = this.obstacles.length - 1; i >= 0; i--) {
        const obstacle = this.obstacles[i];
        obstacle.x -= this.speed * dt;
        if (obstacle.x + obstacle.w < -20) {
          this.obstacles.splice(i, 1);
        }
      }

      for (let i = this.tokens.length - 1; i >= 0; i--) {
        const token = this.tokens[i];
        token.x -= this.speed * dt;
        if (token.x + token.r < -20) {
          this.tokens.splice(i, 1);
          continue;
        }
        if (this.circleRectIntersect(token, this.runnerRect())) {
          this.score += 16;
          this.tokens.splice(i, 1);
        }
      }

      const runnerRect = this.runnerRect();
      for (let i = 0; i < this.obstacles.length; i++) {
        if (this.rectIntersect(this.obstacles[i], runnerRect)) {
          this.finishGame(this.obstacles[i].type);
          return;
        }
      }
    },
    spawnPattern() {
      const baseX = this.canvasWidth + 24;
      const roll = Math.random();

      let type = "outage";
      if (roll < 0.38) {
        type = "outage";
      } else if (roll < 0.7) {
        type = "timeout";
      } else {
        type = "firewall";
      }
      if (this.lastObstacleType === "firewall" && type === "firewall") {
        type = Math.random() < 0.5 ? "outage" : "timeout";
      }
      this.lastObstacleType = type;

      if (type === "outage") {
        this.obstacles.push({
          x: baseX,
          y: this.groundY - 42,
          w: 30,
          h: 42,
          type: "outage",
        });
      } else if (type === "timeout") {
        this.obstacles.push({
          x: baseX,
          y: this.groundY - 30,
          w: 48,
          h: 30,
          type: "timeout",
        });
      } else {
        this.obstacles.push({
          x: baseX,
          y: this.groundY - 64,
          w: 46,
          h: 22,
          type: "firewall",
        });
      }

      if (Math.random() < 0.48) {
        this.tokens.push({
          x: baseX + 66,
          y: this.groundY - (Math.random() < 0.5 ? 72 : 102),
          r: 10,
        });
      }
    },
    finishGame(obstacleType) {
      this.running = false;
      this.gameOver = true;
      this.stopLoop();
      this.gameOverReason = this.mapObstacleToReason(obstacleType);
      const finalScore = Math.floor(this.score);
      if (finalScore > this.bestScore) {
        this.bestScore = finalScore;
        window.localStorage.setItem(STORAGE_KEY, String(finalScore));
      }
      this.render();
    },
    mapObstacleToReason(obstacleType) {
      if (obstacleType === "firewall") {
        return "Request blocked by firewall gate";
      }
      if (obstacleType === "timeout") {
        return "Request timed out on route";
      }
      return "Request failed with 503";
    },
    jump() {
      if (!this.running || !this.runner.onGround) {
        return;
      }
      this.jumpQueued = true;
    },
    setDuck(value) {
      this.duckPressed = value;
    },
    onKeyDown(event) {
      if (event.code === "Space" || event.code === "ArrowUp") {
        event.preventDefault();
        this.jump();
      }
      if (event.code === "ArrowDown") {
        event.preventDefault();
        this.setDuck(true);
      }
    },
    onKeyUp(event) {
      if (event.code === "ArrowDown") {
        event.preventDefault();
        this.setDuck(false);
      }
    },
    runnerRect() {
      return {
        x: this.runner.x,
        y: this.runner.y,
        w: this.runner.width,
        h: this.runner.height,
      };
    },
    rectIntersect(a, b) {
      return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    },
    circleRectIntersect(circle, rect) {
      const closestX = Math.max(rect.x, Math.min(circle.x, rect.x + rect.w));
      const closestY = Math.max(rect.y, Math.min(circle.y, rect.y + rect.h));
      const dx = circle.x - closestX;
      const dy = circle.y - closestY;
      return dx * dx + dy * dy <= circle.r * circle.r;
    },
    drawBackground(ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 0, this.canvasHeight);
      gradient.addColorStop(0, "#102838");
      gradient.addColorStop(1, "#081218");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      ctx.fillStyle = "rgba(42, 125, 98, 0.24)";
      for (let i = 0; i < 8; i++) {
        const x = ((i * 170 - this.worldShiftA) % (this.canvasWidth + 200)) - 50;
        ctx.fillRect(x, this.groundY - 120, 44, 120);
      }

      ctx.fillStyle = "rgba(249, 198, 84, 0.13)";
      for (let i = 0; i < 10; i++) {
        const x = ((i * 130 - this.worldShiftB) % (this.canvasWidth + 140)) - 20;
        ctx.fillRect(x, this.groundY - 62, 26, 62);
      }

      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, this.groundY + 1);
      ctx.lineTo(this.canvasWidth, this.groundY + 1);
      ctx.stroke();
    },
    drawRoundedRect(ctx, x, y, width, height, radius) {
      const safeRadius = Math.min(radius, width / 2, height / 2);
      ctx.beginPath();
      ctx.moveTo(x + safeRadius, y);
      ctx.lineTo(x + width - safeRadius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
      ctx.lineTo(x + width, y + height - safeRadius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
      ctx.lineTo(x + safeRadius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
      ctx.lineTo(x, y + safeRadius);
      ctx.quadraticCurveTo(x, y, x + safeRadius, y);
      ctx.closePath();
    },
    render() {
      const canvas = this.$refs.gameCanvas;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext("2d");
      this.drawBackground(ctx);

      for (let i = 0; i < this.tokens.length; i++) {
        const token = this.tokens[i];
        ctx.fillStyle = "#8df08a";
        ctx.beginPath();
        ctx.arc(token.x, token.y, token.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.stroke();
        ctx.fillStyle = "#12351f";
        ctx.font = "11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("200", token.x, token.y + 4);
      }

      for (let i = 0; i < this.obstacles.length; i++) {
        const obstacle = this.obstacles[i];
        let label = "503";
        if (obstacle.type === "firewall") {
          ctx.fillStyle = "#f6b84f";
          label = "FIREWALL";
        } else if (obstacle.type === "timeout") {
          ctx.fillStyle = "#d67f2f";
          label = "TIMEOUT";
        } else {
          ctx.fillStyle = "#cf3a3a";
        }
        ctx.fillRect(obstacle.x, obstacle.y, obstacle.w, obstacle.h);

        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.fillRect(obstacle.x + 4, obstacle.y + 4, obstacle.w - 8, 5);

        ctx.fillStyle = "#fefefe";
        ctx.font = obstacle.type === "firewall" ? "9px sans-serif" : "11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(label, obstacle.x + obstacle.w / 2, obstacle.y + obstacle.h / 2 + 4);
      }

      ctx.strokeStyle = "rgba(132, 228, 255, 0.38)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(this.runner.x - 70, this.runner.y + this.runner.height / 2);
      ctx.lineTo(this.runner.x - 6, this.runner.y + this.runner.height / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      const runnerBodyColor = this.duckPressed ? "#84d2f6" : "#45a5d8";
      this.drawRoundedRect(
        ctx,
        this.runner.x,
        this.runner.y,
        this.runner.width,
        this.runner.height,
        8
      );
      ctx.fillStyle = runnerBodyColor;
      ctx.fill();

      ctx.fillStyle = "#d8f5ff";
      ctx.font = this.runner.height < this.runner.normalHeight ? "9px monospace" : "11px monospace";
      ctx.textAlign = "center";
      ctx.fillText(
        "REQ",
        this.runner.x + this.runner.width / 2,
        this.runner.y + this.runner.height / 2 + 4
      );

      ctx.fillStyle = "rgba(173, 234, 255, 0.95)";
      ctx.font = "10px monospace";
      ctx.textAlign = "left";
      ctx.fillText("GET /users/me", this.runner.x - 118, this.runner.y + this.runner.height / 2 + 4);

      if (!this.running && !this.gameOver) {
        ctx.fillStyle = "rgba(255,255,255,0.82)";
        ctx.font = "14px sans-serif";
        ctx.fillText("Press Start to begin", 16, 24);
      }
    },
  },
};
</script>

<style scoped>
.maintenance-shell {
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 64px);
  background: radial-gradient(circle at 20% 10%, #214d50 0%, #0b1f2a 45%, #090f15 100%);
  color: #f8f8f2;
  padding: 28px 18px 42px;
}

.aurora {
  position: absolute;
  filter: blur(60px);
  opacity: 0.55;
  pointer-events: none;
}

.aurora-a {
  width: 380px;
  height: 380px;
  background: #21bf73;
  top: -140px;
  left: -80px;
  animation: driftA 12s ease-in-out infinite;
}

.aurora-b {
  width: 360px;
  height: 360px;
  background: #f2c94c;
  bottom: -130px;
  right: -70px;
  animation: driftB 10s ease-in-out infinite;
}

.grid {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: radial-gradient(circle at center, black 35%, transparent 100%);
}

.content {
  position: relative;
  z-index: 1;
  max-width: 920px;
  margin: 0 auto;
  text-align: center;
}

.badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.14);
}

h1 {
  margin: 16px 0 8px;
  font-size: clamp(28px, 5vw, 52px);
  letter-spacing: 0.02em;
}

.subtitle {
  margin: 0 auto 22px;
  max-width: 640px;
  color: #d8e2e5;
}

.game-panel {
  margin: 0 auto;
  max-width: 760px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  background: rgba(4, 15, 23, 0.74);
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
}

.game-head {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
  font-size: 14px;
}

.legend-row {
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.legend-item {
  font-size: 12px;
  border-radius: 999px;
  padding: 5px 8px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.legend-player {
  background: rgba(74, 173, 218, 0.2);
}

.legend-outage {
  background: rgba(207, 58, 58, 0.2);
}

.legend-firewall {
  background: rgba(246, 184, 79, 0.2);
}

.legend-token {
  background: rgba(114, 224, 129, 0.2);
}

.canvas-wrap {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.game-canvas {
  display: block;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 12px;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(6, 14, 22, 0.62);
}

.overlay h3 {
  margin: 0;
}

.overlay p {
  margin: 0;
  font-size: 0.9rem;
}

.controls-row {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.desktop-hint {
  font-size: 0.82rem;
  color: #d9dce0;
}

.mobile-controls {
  display: flex;
  gap: 8px;
}

.hint {
  margin-top: 18px;
  color: #c2d3d9;
  font-size: 13px;
}

@keyframes driftA {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(40px, 26px) scale(1.08);
  }
}

@keyframes driftB {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(-34px, -20px) scale(1.06);
  }
}

@media (max-width: 760px) {
  .maintenance-shell {
    min-height: calc(100vh - 56px);
    padding-top: 16px;
  }

  .game-head {
    font-size: 13px;
  }

  .controls-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .legend-row {
    grid-template-columns: 1fr;
  }
}
</style>
